import React, {useMemo} from "react";
import {Col, Row} from "react-bootstrap";
import {PiArrowDown, PiArrowUp} from "react-icons/pi";
import ChartCard from "./ChartCard.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {formatEuro, formatSignedPercent, TREND_DOWN_COLOR, TREND_UP_COLOR} from "./format.js";

const LIMIT = 8;

function TrendList({title, icon: Icon, color, rows, error}) {
    return (
        <div>
            <div className="d-inline-flex align-items-center gap-1 fw-semibold mb-2">
                <Icon style={{color}} aria-hidden="true" />
                {title}
            </div>
            {error && <div className="text-danger small">{error}</div>}
            {!error && rows.length === 0 && <div className="text-muted small">Keine Artikel mit ausreichender Vergleichsbasis.</div>}
            <ul className="list-unstyled mb-0">
                {rows.map((row) => {
                    // Quasi-Null-Basis im Vorzeitraum: "neu" statt einer
                    // irreführenden vierstelligen Prozentzahl (siehe
                    // SalesStatisticsQuery::trend()).
                    const signed = row.is_new ? "neu" : formatSignedPercent(row.change_pct);
                    return (
                        <li key={row.article_number} className="d-flex align-items-center justify-content-between gap-2 py-1 border-bottom">
                            <span className="text-truncate" style={{maxWidth: "60%"}} title={row.name || row.article_number}>
                                <span className="font-monospace small text-muted me-1">{row.article_number}</span>
                                {row.name || "–"}
                            </span>
                            <span className="text-end small">
                                <span className="d-block fw-semibold" style={{color}}>{signed || "–"}</span>
                                <span className="d-block text-muted">{formatEuro(row.recent_net_amount)}</span>
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

/** #10 Trend - Aufsteiger und Absteiger nebeneinander (letzte 12 gegenüber
 * den vorherigen 12 Monaten). */
export default function TrendPanel({filters}) {
    const gainersParams = useMemo(() => ({...filters, direction: "gainers", limit: LIMIT}), [filters]);
    const losersParams = useMemo(() => ({...filters, direction: "losers", limit: LIMIT}), [filters]);
    const gainers = useStatisticsData("/statistics/trend", gainersParams);
    const losers = useStatisticsData("/statistics/trend", losersParams);
    const loading = gainers.loading || losers.loading;

    return (
        <ChartCard
            title="Trend – Auf- und Absteiger"
            help="Vergleich der letzten 12 gegenüber den vorherigen 12 Monaten (bezogen auf den jüngsten Beleg im Datenbestand), sortiert nach prozentualer Veränderung des Nettoumsatzes. Berücksichtigt werden nur Artikel mit mindestens 500 € Umsatz in einem der beiden Zeiträume – sonst verzerren Kleinstbeträge die Rangliste. Artikel mit negativem Umsatz im aktuellen Zeitraum (Retouren-Überhang) bleiben unberücksichtigt; bei einer Vorjahresbasis nahe null steht „neu“ statt einer irreführenden Prozentzahl."
            loading={loading}
            error={gainers.error || losers.error}
            empty={!loading && (gainers.data?.rows || []).length === 0 && (losers.data?.rows || []).length === 0}
            height={340}
        >
            <Row className="g-4">
                <Col md={6}>
                    <TrendList title="Aufsteiger" icon={PiArrowUp} color={TREND_UP_COLOR} rows={gainers.data?.rows || []} error={gainers.error} />
                </Col>
                <Col md={6}>
                    <TrendList title="Absteiger" icon={PiArrowDown} color={TREND_DOWN_COLOR} rows={losers.data?.rows || []} error={losers.error} />
                </Col>
            </Row>
        </ChartCard>
    );
}
